import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { REGION_VALUES, muscleRegionLabel } from 'components/play/constants';

/**
 * Radar de volumen por región muscular (Mi Rango). SVG sin dependencias, igual que
 * PlayMetricsDonut (@nivo/radar no está instalado y el repo arrastra un conflicto de peers).
 *
 * Cruza dos series ya normalizadas a % del total:
 *   - `actual`: lo que el usuario REGISTRÓ (myMuscleDistribution).
 *   - `target`: lo que su rutina semanal PLANIFICA (myWeeklyRoutine).
 *
 * El punto de la pantalla es que el hueco entre ambas se vea: una región que se planifica y no
 * se entrena hunde el polígono dorado por dentro de la línea de objetivo.
 *
 * `actual`/`target`: [{ region, percent }] con las 7 regiones siempre presentes (volumeByRegion
 * rellena con 0 las vacías).
 */
const SIZE = 260;
const CENTER = SIZE / 2;
const RADIUS = 88;
const LABEL_GAP = 26;
const RINGS = 4;

// Vértice i de un polígono regular de n lados, empezando arriba y girando en sentido horario.
const vertex = (i, n, r) => {
  const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
  return [CENTER + Math.cos(angle) * r, CENTER + Math.sin(angle) * r];
};

const polygon = (radii, n) => radii.map((r, i) => vertex(i, n, r).join(',')).join(' ');

// Constante de módulo: `actual` y `target` son dependencias de sendos useMemo, y un `[]` en la
// firma se recrearía en cada render recalculando el radar.
const DEFAULT_SERIES = [];

function PlayVolumeRadar({
  title,
  actual = DEFAULT_SERIES,
  target = DEFAULT_SERIES,
  emptyLabel = ''
}) {
  const theme = useTheme();
  const { t } = useTranslation(['play']);

  const byRegion = (series) =>
    REGION_VALUES.map((region) => (series || []).find((d) => d.region === region)?.percent || 0);

  const actualValues = useMemo(() => byRegion(actual), [actual]);
  const targetValues = useMemo(() => byRegion(target), [target]);

  const hasActual = actualValues.some((v) => v > 0);
  const hasTarget = targetValues.some((v) => v > 0);

  /**
   * Escala al máximo real de ambas series (no fija a 100): con 7 regiones un reparto perfecto
   * da ~14% cada una, y en un eje 0-100 el polígono quedaría diminuto contra el borde.
   * El +25% de aire evita que el pico máximo toque el anillo exterior.
   */
  const scaleMax = useMemo(() => {
    const peak = Math.max(...actualValues, ...targetValues, 0);
    if (peak <= 0) return 100;
    return Math.ceil((peak * 1.25) / 5) * 5;
  }, [actualValues, targetValues]);

  const n = REGION_VALUES.length;
  const toRadius = (percent) => (Math.min(percent, scaleMax) / scaleMax) * RADIUS;

  if (!hasActual && !hasTarget) {
    return (
      <Box
        sx={{
          backgroundColor: 'surface.content',
          border: '1px solid',
          borderColor: 'hairline.main',
          borderRadius: '16px',
          p: { xs: 2, md: 2.5 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{title}</Typography>
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 180, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }}>
            {emptyLabel}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'surface.content',
        border: '1px solid',
        borderColor: 'hairline.main',
        borderRadius: '16px',
        p: { xs: 2, md: 2.5 },
        height: '100%'
      }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>{title}</Typography>

      <Box
        component="svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={title}
        sx={{ width: '100%', maxWidth: SIZE + 90, height: 'auto', display: 'block', mx: 'auto', overflow: 'visible' }}>
        {/* Rejilla: anillos concéntricos + radios */}
        {Array.from({ length: RINGS }, (_, ring) => {
          const r = (RADIUS / RINGS) * (ring + 1);
          return (
            <polygon
              key={r}
              points={polygon(Array(n).fill(r), n)}
              fill="none"
              stroke={theme.palette.hairline.main}
              strokeWidth={1}
            />
          );
        })}
        {REGION_VALUES.map((region, i) => {
          const [x, y] = vertex(i, n, RADIUS);
          return <line key={region} x1={CENTER} y1={CENTER} x2={x} y2={y} stroke={theme.palette.hairline.main} strokeWidth={1} />;
        })}

        {/* Objetivo (rutina planificada): gris punteado, por debajo del dorado */}
        {hasTarget && (
          <polygon
            points={polygon(targetValues.map(toRadius), n)}
            fill="none"
            stroke={theme.palette.hairline.strong}
            strokeWidth={1.5}
            strokeDasharray="4 4"
            strokeLinejoin="round"
          />
        )}

        {/* Tu volumen (resultados registrados) */}
        {hasActual && (
          <>
            <polygon
              points={polygon(actualValues.map(toRadius), n)}
              fill={theme.palette.gold.soft}
              stroke={theme.palette.gold.main}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {actualValues.map((v, i) => {
              const [x, y] = vertex(i, n, toRadius(v));
              return <circle key={REGION_VALUES[i]} cx={x} cy={y} r={3} fill={theme.palette.gold.main} />;
            })}
          </>
        )}

        {/* Etiquetas de vértice */}
        {REGION_VALUES.map((region, i) => {
          const [x, y] = vertex(i, n, RADIUS + LABEL_GAP);
          // Anclaje según el lado del círculo, para que el texto no invada el gráfico.
          let anchor = 'middle';
          if (x > CENTER + 4) anchor = 'start';
          else if (x < CENTER - 4) anchor = 'end';
          return (
            <text
              key={region}
              x={x}
              y={y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fill={theme.palette.text.secondary}
              style={{ fontSize: 11, fontWeight: 700 }}>
              {muscleRegionLabel(t, region)}
            </text>
          );
        })}
      </Box>

      <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 3, borderRadius: 2, backgroundColor: 'gold.main' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t('rank.radarYours', { ns: 'play', defaultValue: 'Tu volumen' })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 14,
              height: 0,
              borderTop: '2px dashed',
              borderColor: 'hairline.strong'
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t('rank.radarTarget', { ns: 'play', defaultValue: 'Objetivo' })}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

PlayVolumeRadar.propTypes = {
  title: PropTypes.string.isRequired,
  actual: PropTypes.arrayOf(PropTypes.shape({ region: PropTypes.string, percent: PropTypes.number })),
  target: PropTypes.arrayOf(PropTypes.shape({ region: PropTypes.string, percent: PropTypes.number })),
  emptyLabel: PropTypes.string
};

export default PlayVolumeRadar;
